import React, { FC, useState, useEffect, ReactNode } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Form as FinalForm, FormRenderProps } from 'react-final-form';
import { useMtsModal } from '@mts-ds/components-react';
import useRequestHandler from '../../../../../../hooks/useRequestHandler';

import { Dispatch, iRootState } from '../../../../../../store';
import { IId, IUser } from '../../../../../../models/types';
import { getTimeFromMinutes, getMinutesFromTime } from '../../../../../../utils/convertTime';
import { paths } from '../../../../../Root/paths';
import { mapColor } from '../../../../../../theme';

import FormBase from '../../../../../../components_/Form';
import Modal from '../../../../../../components_/Modal';
import DrawerContentItemArea from '../../../../../../components_/Drawer/DrawerContentItemArea';

import FormDraw from './FormDraw';
import FormInfo from './FormInfo';
import FormAccess from './FormAccess';
import FormDelete from './FormDelete';
import FormActions from './FormActions';
import FormCoordinates from './FormCoordinates';

const mapState = (state: iRootState) => ({
  data: state.infrastructureAreas.data,
  map: state.infrastructureMap.map,
  area: state.infrastructureAreas.item,
  type: state.infrastructureMap.type,
  coordinates: state.infrastructureAreas.coordinates,
  isGeo: state.infrastructureMap.layers.isGeo
});

const mapDispatch = (dispatch: any) => {
  const d = dispatch as Dispatch;

  return {
    getDataUsers: d.infrastructureAreas.getDataUsers,
    resetDataUsers: d.infrastructureAreas.resetDataUsers,
    deleteArea: d.infrastructureAreas.deleteArea,
    updateOrCreateArea: d.infrastructureAreas.updateOrCreateArea,
    setBoxCreateMode: d.infrastructureMap.setBoxCreateMode,
    setCircleCreateMode: d.infrastructureMap.setCircleCreateMode,
    setPolygonCreateMode: d.infrastructureMap.setPolygonCreateMode,
    resetFocus: d.infrastructureMap.resetFocus
  };
};

type connectedProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export type IProps = connectedProps;

interface IFormValues {
  // id: IId;
  name: string;
  descr: string;
  dangerClassId: IId | null;
  mapColor: string;
  workStart: string;
  workEnd: string;
  allowedHours: number;
  allowedMinutes: number;
  /** Form control may yield string from number input; empty → null on save */
  immobilizedTimeSeconds: number | string | null;
  protectionsList: IId[];
  denyFrom: IId[];
  appliance: IId;
  employees: IUser[];
  employeesList: IId[];
  position: number[] | null;
  geomCoords: number[] | null;
}

interface IParams {
  officeId: string;
}

type IRenderForm = (props: FormRenderProps<IFormValues>) => ReactNode;

const defaultValues: IFormValues = {
  // id: 0,
  name: '',
  descr: '',
  dangerClassId: -1,
  mapColor: mapColor.greySteel,
  workStart: '',
  workEnd: '',
  allowedHours: 0,
  allowedMinutes: 0,
  immobilizedTimeSeconds: '',
  protectionsList: [],
  denyFrom: [],
  appliance: 'GENERIC',
  employees: [],
  employeesList: [],
  position: null,
  geomCoords: null
};

const Form: FC<IProps> = ({
  data,
  map,
  area,
  type,
  coordinates,
  getDataUsers,
  resetDataUsers,
  deleteArea,
  updateOrCreateArea,
  setBoxCreateMode,
  setCircleCreateMode,
  setPolygonCreateMode,
  resetFocus,
  isGeo
}) => {
  const { id: currentMapId } = map;

  const { officeId } = useParams<IParams>();

  const history = useHistory();
  const requestHandler = useRequestHandler();
  const [initialValues, setInitialValues] = useState(defaultValues);

  const [openDeleteModal] = useMtsModal(Modal, {
    header: 'Удалить зону',
    text: 'Вы действительно хотите удалить зону?',
    buttonTextConfirm: 'Удалить',
    ...(area && {
      onMtsModalConfirm: async () => {
        const error = await deleteArea(area.id);

        requestHandler({
          error,
          entity: 'area',
          onSuccess: () => {
            history.push(paths.map.office.areas.root);
          },
          isDelete: true
        });
      }
    })
  });

  const isCreate = !Boolean(area);
  const isEdit = Boolean(area);

  useEffect(() => {
    if (area) {
      const { workDurationMinutes, geomGlobalCoords, geomCoords, immobilizedTimeSeconds } = area;
      const position = isGeo ? geomGlobalCoords : geomCoords;

      const [allowedHours, allowedMinutes] = getTimeFromMinutes(workDurationMinutes);

      setInitialValues({
        ...area,
        position,
        allowedHours,
        allowedMinutes,
        // Backward compatible: missing field on old backends → empty control
        immobilizedTimeSeconds: immobilizedTimeSeconds ?? ''
      });
    }
  }, [area, isGeo]);

  const goBack = () => {
    resetFocus(undefined);
    history.goBack();
  };

  const onSubmit = async (values: IFormValues) => {
    // tslint:disable-next-line: no-shadowed-variable
    const {
      allowedHours,
      allowedMinutes,
      employees,
      position,
      immobilizedTimeSeconds: immobilizedRaw,
      ...data
    } = values;

    // tslint:disable-next-line: no-shadowed-variable
    const workDurationMinutes = getMinutesFromTime(allowedHours, allowedMinutes) || null;
    const isWorkingZone =
      Boolean(data.appliance) && data.appliance === workingApplianceSystemName;
    // Non-working zones must not persist a hidden timer value
    const immobilizedTimeSeconds = (() => {
      if (!isWorkingZone) {
        return null;
      }

      if (immobilizedRaw === '' || immobilizedRaw === null || immobilizedRaw === undefined) {
        return null;
      }

      const parsed = Number(immobilizedRaw);
      return Number.isFinite(parsed) ? parsed : null;
    })();
    const mapId = area ? area.mapId : currentMapId;
    const employeesList = (employees || []).map(({ id }) => id);

    const positionToSave =
      Array.isArray(coordinates) && coordinates.length ? (coordinates as any) : position;

    const body = {
      ...data,
      geomGlobalCoords: isGeo ? positionToSave : null,
      geomCoords: isGeo ? null : positionToSave,
      workDurationMinutes,
      immobilizedTimeSeconds,
      mapId,
      geomType: type,
      employeesList
    };

    const error = await updateOrCreateArea(body);

    requestHandler({
      error,
      entity: 'area',
      onSuccess: goBack,
      isCreate,
      isEdit
    });
  };

  const workingApplianceSystemName = React.useMemo(() => {
    const item = data.areasAppliance.find((a) =>
      a.friendlyName ? a.friendlyName.toLowerCase().includes('рабоч') : false
    );
    return item?.systemName;
  }, [data.areasAppliance]);

  const renderForm: IRenderForm = ({ handleSubmit, values }) => {
    const isWorkingZone =
      Boolean(values.appliance) && values.appliance === workingApplianceSystemName;
    return (
      <FormBase onSubmit={handleSubmit}>
        <DrawerContentItemArea area={area} color={values.mapColor} variant="form" />
        {isWorkingZone && (
          <FormDraw
            area={area}
            type={type}
            setBoxCreateMode={() => setBoxCreateMode(undefined)}
            setCircleCreateMode={() => setCircleCreateMode(undefined)}
            setPolygonCreateMode={() => setPolygonCreateMode(undefined)}
          />
        )}
        <FormInfo data={data} />
        {isWorkingZone && (
          <FormAccess getDataUsers={getDataUsers} resetDataUsers={resetDataUsers} data={data} />
        )}
        {area && <FormDelete onOpenDeleteModal={openDeleteModal} />}
        <FormActions isCreate={Boolean(area)} onGoBack={goBack} />
        {<FormCoordinates coordinates={coordinates} />}
      </FormBase>
    );
  };


  return (
    <FinalForm
      key={`${area ? String(area.id) : 'create'}:${isGeo ? 'geo' : 'indoor'}`}
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={renderForm}
    />
  );
};

const withConnect = connect(mapState, mapDispatch);

export default withConnect(Form);
